import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ArrowLeft, ExternalLink, Star, PlayCircle, Loader2,
  Tv, Film, BookOpen, Sparkles, CheckCircle2, Clock,
  Eye, XCircle, PauseCircle, ArrowDownUp, ArrowUp, ArrowDown, ChevronDown
} from 'lucide-react';
import { userApi } from '../api';
import SortControl from './SortControl';

// ──────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ──────────────────────────────────────────────────────────────────────────────
const TYPE_ORDER = { TV: 1, ONA: 2, OVA: 3, Movie: 4, Special: 5 };

const TYPE_META = {
  TV: { label: 'Série TV', icon: Tv, color: 'from-blue-600 to-blue-800', border: 'border-blue-500/40', text: 'text-blue-300', badge: 'bg-blue-600/20 text-blue-300 border border-blue-500/40' },
  Movie: { label: 'Film', icon: Film, color: 'from-purple-600 to-purple-800', border: 'border-purple-500/40', text: 'text-purple-300', badge: 'bg-purple-600/20 text-purple-300 border border-purple-500/40' },
  OVA: { label: 'OVA', icon: Sparkles, color: 'from-amber-600 to-amber-800', border: 'border-amber-500/40', text: 'text-amber-300', badge: 'bg-amber-600/20 text-amber-300 border border-amber-500/40' },
  ONA: { label: 'ONA', icon: Sparkles, color: 'from-teal-600 to-teal-800', border: 'border-teal-500/40', text: 'text-teal-300', badge: 'bg-teal-600/20 text-teal-300 border border-teal-500/40' },
  Special: { label: 'Spécial', icon: BookOpen, color: 'from-rose-600 to-rose-800', border: 'border-rose-500/40', text: 'text-rose-300', badge: 'bg-rose-600/20 text-rose-300 border border-rose-500/40' },
};

const STATUS_META = {
  watching: { label: 'En cours', icon: Eye, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  completed: { label: 'Complété', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10' },
  plan_to_watch: { label: 'Watchlist', icon: Clock, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  on_hold: { label: 'En pause', icon: PauseCircle, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  dropped: { label: 'Abandonné', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
};

const getTypeMeta = (type) => TYPE_META[type] || TYPE_META.TV;

// ──────────────────────────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────────────────────────
const ProgressBar = ({ value, total, colorClass = 'bg-blue-500' }) => {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className="w-full bg-gray-700/50 rounded-full h-1 overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// ANIME DETAIL MODAL
// ──────────────────────────────────────────────────────────────────────────────
const AnimeDetailModal = ({ anime, onClose, onUpdateAnime }) => {
  const meta = getTypeMeta(anime.type);
  const status = STATUS_META[anime.user_status];
  const totalEps = anime.total_episodes > 0 ? anime.total_episodes : (anime.latest_voiranime_ep > 0 ? anime.latest_voiranime_ep : 0);
  const isEstimatedEps = !anime.total_episodes && anime.latest_voiranime_ep > 0;
  const progressPct = totalEps > 0
    ? Math.min(100, Math.round((anime.watched_episodes_count / totalEps) * 100))
    : 0;

  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowStatusMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusChange = async (newStatus) => {
    setShowStatusMenu(false);
    if (newStatus === anime.user_status) return;
    try {
      setIsUpdating(true);
      await userApi.updateAnimeStatus(anime.id, newStatus);
      if (onUpdateAnime) {
        onUpdateAnime({ ...anime, user_status: newStatus });
      }
    } catch (err) {
      console.error('Failed to update status', err);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-2xl bg-gray-900 rounded-2xl border border-gray-700/60 shadow-2xl overflow-hidden"
        style={{ animation: 'fadeIn .18s ease' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Top color bar */}
        <div className={`h-1 w-full bg-gradient-to-r ${meta.color}`} />

        <div className="flex flex-col sm:flex-row gap-6 p-6">
          {/* Cover */}
          <div className="flex-shrink-0 w-full sm:w-40">
            <img src={anime.cover_image_url || '/placeholder.png'} alt={anime.romaji}
              className="w-full rounded-xl object-cover aspect-[2/3] shadow-xl" />
          </div>

          <div className="flex-1 flex flex-col gap-3">
            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${meta.badge}`}>{meta.label}</span>
              
              <div className="relative" ref={menuRef}>
                <button 
                  onClick={() => setShowStatusMenu(!showStatusMenu)}
                  disabled={isUpdating}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded transition-all
                    ${status ? `${status.bg} ${status.color} hover:bg-opacity-80 border border-transparent` : 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-600'}
                    ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {isUpdating ? <Loader2 size={12} className="animate-spin" /> : (status ? <status.icon size={12} /> : <div className="w-2 h-2 rounded-full bg-gray-500" />)}
                  <span>{status ? status.label : 'Aucun statut'}</span>
                  <ChevronDown size={12} className={`transition-transform ${showStatusMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {showStatusMenu && (
                  <div className="absolute top-full left-0 mt-1 w-44 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-20">
                    <button onClick={() => handleStatusChange(null)} className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-400 hover:bg-gray-700 transition-colors">
                      Aucun statut
                    </button>
                    {Object.entries(STATUS_META).map(([key, st]) => (
                      <button 
                        key={key}
                        onClick={() => handleStatusChange(key)}
                        className={`w-full text-left px-3 py-2 text-xs font-semibold flex items-center gap-2 transition-colors hover:bg-gray-700 ${st.color} ${anime.user_status === key ? 'bg-gray-700/50' : ''}`}
                      >
                        <st.icon size={14} /> {st.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div>
              <h3 className="text-xl font-bold text-white leading-tight">{anime.english || anime.romaji || anime.japanese}</h3>
              {anime.romaji && anime.romaji !== (anime.english || anime.romaji || anime.japanese) && (
                <p className="text-sm text-gray-400 mt-0.5">{anime.romaji}</p>
              )}
              {anime.release_date && (
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(anime.release_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' })}
                </p>
              )}
            </div>

            {/* Synopsis */}
            {anime.synopsis && (
              <p className="text-sm text-gray-300 leading-relaxed line-clamp-4">{anime.synopsis}</p>
            )}

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-2 mt-auto">
              <div className="bg-gray-800/60 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-1">Score MAL</p>
                {anime.score ? (
                  <div className="flex items-center gap-1 text-yellow-400">
                    <Star size={13} className="fill-current" />
                    <span className="text-lg font-bold">{anime.score.toFixed(2)}</span>
                  </div>
                ) : <span className="text-gray-500 text-sm">N/A</span>}
              </div>
              <div className="bg-gray-800/60 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-1">Ma note</p>
                {anime.user_rating != null ? (
                  <div className="flex items-center gap-1 text-yellow-400">
                    <Star size={13} className="fill-current" />
                    <span className="text-lg font-bold">{anime.user_rating}</span>
                    <span className="text-gray-500 text-xs">/ 10</span>
                  </div>
                ) : <span className="text-gray-500 text-sm">Non noté</span>}
              </div>
              <div className="bg-gray-800/60 rounded-xl p-3 col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs text-gray-500">Progression</p>
                  <span className="text-xs text-gray-400">
                    {anime.watched_episodes_count} / {totalEps > 0 ? `${isEstimatedEps ? '~' : ''}${totalEps}` : '?'} eps · {progressPct}%
                  </span>
                </div>
                <ProgressBar value={anime.watched_episodes_count} total={totalEps} colorClass={`bg-gradient-to-r ${meta.color}`} />
              </div>
            </div>

            {/* Links */}
            <div className="flex gap-2">
              {anime.mal_id && (
                <a href={`https://myanimelist.net/anime/${anime.mal_id}`} target="_blank" rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl transition-colors">
                  <ExternalLink size={14} /> MyAnimeList
                </a>
              )}
              {anime.voiranime_url && (
                <a href={anime.voiranime_url} target="_blank" rel="noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-xl transition-colors">
                  <PlayCircle size={14} /> VoirAnime
                </a>
              )}
            </div>
          </div>
        </div>

        <button onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-full p-1.5 transition-colors">
          <XCircle size={18} />
        </button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// ANIME CARD  (compact grid card)
// ──────────────────────────────────────────────────────────────────────────────
const AnimeCard = ({ anime, onClick }) => {
  const meta = getTypeMeta(anime.type);
  const status = STATUS_META[anime.user_status];
  const totalEps = anime.total_episodes > 0 ? anime.total_episodes : (anime.latest_voiranime_ep > 0 ? anime.latest_voiranime_ep : 0);
  const isEstimatedEps = !anime.total_episodes && anime.latest_voiranime_ep > 0;
  const pct = totalEps > 0 ? Math.min(100, Math.round((anime.watched_episodes_count / totalEps) * 100)) : 0;

  return (
    <button onClick={onClick}
      className={`group relative rounded-xl border ${meta.border} bg-gray-800/40 overflow-hidden hover:bg-gray-700/50 hover:scale-[1.02] transition-all duration-200 text-left flex flex-col shadow-lg`}>

      {/* Gradient top bar */}
      <div className={`h-0.5 w-full bg-gradient-to-r ${meta.color}`} />

      {/* Cover */}
      <div className="relative w-full aspect-[2/3] overflow-hidden">
        <img src={anime.cover_image_url || '/placeholder.png'} alt={anime.romaji}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

        {/* Type badge overlay */}
        <div className="absolute top-2 left-2">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded backdrop-blur-sm ${meta.badge}`}>
            {meta.label}
          </span>
        </div>

        {/* Score overlay (bottom right) */}
        {anime.score && (
          <div className="absolute bottom-2 right-2 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm px-1.5 py-0.5 rounded text-yellow-400">
            <Star size={10} className="fill-current" />
            <span className="text-[10px] font-bold">{anime.score.toFixed(1)}</span>
          </div>
        )}

        {/* Status overlay (bottom left) */}
        {status && (
          <div className={`absolute bottom-2 left-2 text-[10px] font-semibold px-1.5 py-0.5 rounded backdrop-blur-sm ${status.bg} ${status.color}`}>
            {status.label}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3 flex flex-col gap-2 flex-1">
        <p className="text-xs font-bold text-gray-100 leading-snug line-clamp-2">
          {anime.english || anime.romaji || anime.japanese}
        </p>

        {/* Episode count */}
        <p className="text-[10px] text-gray-500">
          {totalEps > 0 ? `${isEstimatedEps ? '~' : ''}${totalEps} éps` : '? éps'}
          {anime.release_date && ` · ${new Date(anime.release_date).getFullYear()}`}
        </p>

        {/* Progress bar */}
        <ProgressBar value={anime.watched_episodes_count} total={totalEps} colorClass={`bg-gradient-to-r ${meta.color}`} />

        {/* My rating */}
        {anime.user_rating != null && (
          <div className="flex items-center gap-1 text-yellow-400 mt-auto">
            <Star size={11} className="fill-current" />
            <span className="text-xs font-bold">{anime.user_rating}</span>
            <span className="text-gray-600 text-[10px]">/ 10</span>
          </div>
        )}
      </div>
    </button>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// FRANCHISE PAGE
// ──────────────────────────────────────────────────────────────────────────────
const FranchisePage = ({ franchiseId }) => {
  const [franchise, setFranchise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAnime, setSelectedAnime] = useState(null);
  const [sortBy, setSortBy] = useState('order');
  const [sortDir, setSortDir] = useState('asc');

  const handleUpdateAnime = (updatedAnime) => {
    setSelectedAnime(updatedAnime);
    setFranchise(prev => {
      if (!prev) return prev;
      const newFranchise = { ...prev };
      if (newFranchise.series) {
        newFranchise.series = newFranchise.series.map(series => ({
          ...series,
          seasons: series.seasons.map(season => ({
            ...season,
            animes: season.animes.map(a => a.id === updatedAnime.id ? updatedAnime : a)
          }))
        }));
      }
      return newFranchise;
    });
  };

  useEffect(() => {
    const fetchFranchise = async () => {
      try {
        setLoading(true);
        const res = await userApi.getFranchiseDetails(franchiseId);
        setFranchise(res.data);
      } catch (err) {
        console.error('Failed to fetch franchise details', err);
      } finally {
        setLoading(false);
      }
    };
    if (franchiseId) fetchFranchise();
  }, [franchiseId]);

  // Flatten all animes from all seasons
  const allAnimes = useMemo(() => {
    if (!franchise || !franchise.series) return [];
    return franchise.series.flatMap(series => 
      series.seasons.flatMap(s => s.animes.map(a => ({ 
        ...a, 
        season_name: s.name, 
        season_num: s.num,
        series_name: series.name, 
        series_order: series.order,
        relation_type: series.relation_type 
      })))
    );
  }, [franchise]);

  // Sort
  const sortedAnimes = useMemo(() => {
    const sorted = [...allAnimes].sort((a, b) => {
      let result = 0;
      if (sortBy === 'order') {
        const orderA = a.series_order || 999;
        const orderB = b.series_order || 999;
        if (orderA !== orderB) {
          result = orderA - orderB;
        } else {
          const numA = a.season_num || 999;
          const numB = b.season_num || 999;
          if (numA !== numB) {
            result = numA - numB;
          } else {
            result = a.mal_id - b.mal_id;
          }
        }
      } else if (sortBy === 'type') {
        result = (TYPE_ORDER[a.type] || 99) - (TYPE_ORDER[b.type] || 99);
        // secondary sort by mal_id within same type
        if (result === 0) result = (a.mal_id || 0) - (b.mal_id || 0);
      } else if (sortBy === 'release_date') {
        const da = a.release_date ? new Date(a.release_date).getTime() : Number.MAX_SAFE_INTEGER;
        const db = b.release_date ? new Date(b.release_date).getTime() : Number.MAX_SAFE_INTEGER;
        result = da - db;
      } else if (sortBy === 'mal_id') {
        result = (a.mal_id || 999999) - (b.mal_id || 999999);
      }
      return sortDir === 'asc' ? result : -result;
    });
    return sorted;
  }, [allAnimes, sortBy, sortDir]);

  // Global stats
  const stats = useMemo(() => {
    let hasUnknownEps = false;
    const totalEps = allAnimes.reduce((s, a) => {
      const eps = a.total_episodes > 0 ? a.total_episodes : (a.latest_voiranime_ep > 0 ? a.latest_voiranime_ep : 0);
      if (eps === 0) hasUnknownEps = true;
      return s + eps;
    }, 0);
    const watchedEps = allAnimes.reduce((s, a) => s + (a.watched_episodes_count || 0), 0);
    const rated = allAnimes.filter(a => a.user_rating != null);
    const avgRating = rated.length ? (rated.reduce((s, a) => s + a.user_rating, 0) / rated.length).toFixed(1) : null;
    const completed = allAnimes.filter(a => a.user_status === 'completed').length;
    const tvCount = allAnimes.filter(a => ['TV', 'OVA', 'ONA'].includes(a.type)).length;
    const movieCount = allAnimes.filter(a => a.type === 'Movie').length;
    return { totalEps, watchedEps, avgRating, totalAnimes: allAnimes.length, completed, tvCount, movieCount, hasUnknownEps };
  }, [allAnimes]);

  // Sections
  const mainChain = sortedAnimes.filter(a => !a.relation_type);
  const spinOffs = sortedAnimes.filter(a => !!a.relation_type && !['Special', 'Movie'].includes(a.type));
  const specials = sortedAnimes.filter(a => !!a.relation_type && ['Special', 'Movie'].includes(a.type));

  const hasOnlyMainChain = mainChain.length > 0 && spinOffs.length === 0 && specials.length === 0;

  const renderSection = (title, animes, hideSectionTitle = false, isHalfWidth = false) => {
    if (!animes || animes.length === 0) return null;
    
    // Group by series_name
    const groupedBySeries = animes.reduce((acc, anime) => {
      const sName = anime.series_name || 'Autre';
      if (!acc[sName]) acc[sName] = [];
      acc[sName].push(anime);
      return acc;
    }, {});

    // Maintain the sorted order of series
    const seriesKeys = Object.keys(groupedBySeries).sort((a, b) => {
      const firstA = groupedBySeries[a][0];
      const firstB = groupedBySeries[b][0];
      return animes.indexOf(firstA) - animes.indexOf(firstB);
    });

    const hasMultipleSeries = seriesKeys.length > 1;

    // Use smaller grid for cards if there are multiple series side-by-side
    const gridClasses = hasMultipleSeries
      ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-3"
      : "grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3";

    return (
      <div className="mb-12">
        {!hideSectionTitle && (
          <h3 className="text-xl font-bold text-white mb-6 border-b border-gray-700/60 pb-2 flex items-center">
            {title} <span className="text-sm font-normal text-gray-500 ml-2">({animes.length})</span>
          </h3>
        )}
        
        <div className={hasMultipleSeries ? "grid grid-cols-1 lg:grid-cols-2 gap-8" : "flex flex-col gap-8"}>
          {seriesKeys.map(seriesName => {
            const seriesAnimes = groupedBySeries[seriesName];
            const sortedSeriesAnimes = [...seriesAnimes].sort((a, b) => {
              // prioritize TV
              if (a.type === 'TV' && b.type !== 'TV') return -1;
              if (b.type === 'TV' && a.type !== 'TV') return 1;
              
              // then by start_date
              const timeA = new Date(a.start_date || "9999-12-31").getTime();
              const timeB = new Date(b.start_date || "9999-12-31").getTime();
              if (timeA !== timeB) return timeA - timeB;
              
              // fallback to mal_id
              return (a.mal_id || 0) - (b.mal_id || 0);
            });
            const mainAnime = sortedSeriesAnimes[0];
            const displaySeriesName = mainAnime ? (mainAnime.english || mainAnime.romaji || mainAnime.french || seriesName) : seriesName;

            return (
              <div key={seriesName} className={hasMultipleSeries ? "bg-gray-800/20 p-4 rounded-xl border border-gray-800/50" : ""}>
                {hasMultipleSeries && (
                  <h4 className="text-sm font-bold text-gray-300 mb-4 flex items-center gap-2">
                    <div className="w-1 h-4 bg-blue-500 rounded-full"></div>
                    {displaySeriesName}
                  </h4>
                )}
                <div className={gridClasses}>
                  {seriesAnimes.map(anime => (
                    <AnimeCard key={anime.id} anime={anime} onClick={() => setSelectedAnime(anime)} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-blue-500">
      <Loader2 size={48} className="animate-spin mb-4" />
      <p className="text-gray-400 animate-pulse">Chargement de la franchise...</p>
    </div>
  );

  if (!franchise) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white">
      <h2 className="text-2xl font-bold mb-4">Franchise introuvable</h2>
      <button onClick={() => window.location.hash = 'dashboard'} className="text-blue-400 hover:underline flex items-center gap-2">
        <ArrowLeft size={16} /> Retour à la bibliothèque
      </button>
    </div>
  );

  const sortedAllAnimes = [...allAnimes].sort((a, b) => {
    // prioritize TV
    if (a.type === 'TV' && b.type !== 'TV') return -1;
    if (b.type === 'TV' && a.type !== 'TV') return 1;
    
    // then by start_date
    const timeA = new Date(a.start_date || "9999-12-31").getTime();
    const timeB = new Date(b.start_date || "9999-12-31").getTime();
    if (timeA !== timeB) return timeA - timeB;
    
    // fallback to mal_id
    return (a.mal_id || 0) - (b.mal_id || 0);
  });

  const mainFranchiseAnime = sortedAllAnimes[0];
  const franchiseDisplayTitle = mainFranchiseAnime ? (mainFranchiseAnime.english || mainFranchiseAnime.romaji || mainFranchiseAnime.french || franchise.name) : franchise.name;

  return (
    <div className="min-h-screen bg-gray-900 text-white relative">

      {/* ── HERO BANNER ── */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        {franchise.cover_image_url && (
          <img src={franchise.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-sm opacity-25" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-gray-900" />

        <div className="relative z-10 h-full w-full lg:w-[85%] xl:w-[75%] mx-auto px-4 flex flex-col justify-end pb-6">
          <button
            onClick={() => window.location.hash = 'dashboard'}
            className="absolute top-5 left-4 text-gray-300 hover:text-white flex items-center gap-2 bg-gray-900/50 hover:bg-gray-800 px-3 py-1.5 rounded-lg transition-colors backdrop-blur-sm text-sm font-medium"
          >
            <ArrowLeft size={16} /> Retour
          </button>

          <div className="flex items-end gap-5">
            <div className="hidden sm:block flex-shrink-0 w-32 md:w-40 -mb-14 shadow-2xl ring-2 ring-white/10 rounded-xl overflow-hidden">
              <img src={franchise.cover_image_url || '/placeholder.png'} alt={franchiseDisplayTitle} className="w-full object-cover aspect-[2/3]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white drop-shadow-xl">{franchiseDisplayTitle}</h1>
              <p className="text-gray-400 text-sm mt-1">
                {[
                  stats.tvCount > 0 ? `${stats.tvCount} saison${stats.tvCount > 1 ? 's' : ''}` : null,
                  stats.movieCount > 0 ? `${stats.movieCount} film${stats.movieCount > 1 ? 's' : ''}` : null
                ].filter(Boolean).join(' · ') || `${stats.totalAnimes} entrées`}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-[85%] xl:w-[75%] mx-auto px-4">

        {/* ── STATS BAR ── */}
        <div className="mt-16 sm:mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Entrées', value: stats.totalAnimes, color: 'text-white' },
            { label: 'Épisodes vus', value: `${stats.watchedEps} / ${stats.hasUnknownEps ? '?' : stats.totalEps}`, color: 'text-blue-400' },
            { label: 'Complétées', value: stats.completed, color: 'text-green-400' },
            { label: 'Note moyenne', value: stats.avgRating ? `${stats.avgRating} / 10` : '—', color: 'text-yellow-400' },
          ].map(s => (
            <div key={s.label} className="bg-gray-800/50 border border-gray-700/40 rounded-xl p-4 text-center">
              <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Synopsis */}
        {franchise.description && (
          <p className="mt-5 text-gray-400 text-sm leading-relaxed max-w-3xl line-clamp-3">{franchise.description}</p>
        )}

        {/* ── GRID HEADER (sort) ── */}
        <div className="flex items-center justify-between mt-8 mb-4">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500">
            {sortedAnimes.length} entrée{sortedAnimes.length > 1 ? 's' : ''}
          </h2>
          <SortControl 
            sortBy={sortBy} 
            sortDir={sortDir} 
            onChange={(k, d) => { setSortBy(k); setSortDir(d); }}
            options={[
              { key: 'order', label: 'Chronologique' },
              { key: 'type', label: 'Par Type' },
              { key: 'release_date', label: 'Date de sortie' },
              { key: 'mal_id', label: 'MAL ID' },
            ]}
          />
        </div>

        {/* ── SECTIONS ── */}
        <div className="pb-20">
          {renderSection('Anime', mainChain, hasOnlyMainChain)}
          {renderSection('Spin-offs & Histoires Parallèles', spinOffs)}
          {renderSection('Films & Spéciaux', specials)}
        </div>
      </div>

      {/* Modal */}
      {selectedAnime && (
        <AnimeDetailModal 
          anime={selectedAnime} 
          onClose={() => setSelectedAnime(null)} 
          onUpdateAnime={handleUpdateAnime}
        />
      )}
    </div>
  );
};

export default FranchisePage;
